/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-20 07:47:45
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Field } from 'redux-form';
import { Stack, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { hasTag, isExceedLength } from 'src/helpers/form-validation-helper';
import { OLD_VERSION_TAB, DEFAULT_THEME_TAB } from 'src/constants/translation';

type Props = {
  items: Array<Object>,
  locale: string
};
type ComposedProps = Props & WithI18nProps;

class CommonTab extends Component<ComposedProps> {
  exceedFiftyCharacters = isExceedLength(50);

  renderHelpText = () => {
    const { items, i18n } = this.props;
    const tab = _.get(items, '0.tab');

    if ([OLD_VERSION_TAB, DEFAULT_THEME_TAB].includes(tab)) {
      return (
        <div style={{ marginBottom: '1.25rem' }}>
          {i18n.translate(`AdminHTML.Translation.${tab}.description`, {
            t1: (
              <TextStyle variation="strong">
                {i18n.translate(`AdminHTML.Translation.${tab}.description_t1`)}
              </TextStyle>
            )
          })}
        </div>
      );
    }
  };

  render() {
    const { items = [] } = this.props;
    const groups = _.groupBy(items, item => item.group || 'default');

    return (
      <div>
        {this.renderHelpText()}
        {_.map(groups, (group, index) => (
          <div className="tab-section" key={index}>
            {this.renderGroup(group)}
          </div>
        ))}
      </div>
    );
  }

  /**
   * Get validate rule
   * @param {*} name
   * @returns
   */
  getValidateRule = (name: string) => {
    switch (name) {
      case 'inCollectionSearch':
        return [hasTag, this.exceedFiftyCharacters];
      default:
        return [hasTag];
    }
  };

  /**
   * Render some fields as a group in one tab
   * @param {*} fields
   */
  renderGroup = (items: Array<Object>) => {
    const { i18n } = this.props;
    const firstItem = _.head(items);
    const group = _.get(firstItem, 'group', '');

    return (
      <>
        {group && (
          <div className="tab-section__title">
            {i18n.translate(`AdminHTML.Translation.Group.${group}`)}
          </div>
        )}
        <div className="tab-section__body">
          {_(items)
            .chunk(2)
            .map((row, index) => {
              const [first, second] = row;

              return (
                <div key={index} className={index === 0 ? 'first' : ''}>
                  <Stack distribution="fill" wrap={true} alignment="trailing" key={index}>
                    <div>{first ? this.renderField(first) : ''}</div>
                    <div>{second ? this.renderField(second) : ''}</div>
                  </Stack>
                </div>
              );
            })
            .value()}
        </div>
      </>
    );
  };

  /**
   * Render field
   * @param {*} item
   * @returns
   */
  renderField = (item: Object) => {
    const { locale } = this.props;
    const { name, label, placeholder, multiline } = item;
    const validateList = this.getValidateRule(name);
    return (
      <div>
        <Field
          name={`${locale}.${name}`}
          component={RenderTextField}
          label={label}
          placeholder={placeholder}
          validate={validateList}
          anotherProps={{ multiline }}
        />
      </div>
    );
  };
}

export default withI18n()(CommonTab);
